import { Dispatch, FC, Fragment, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Tree, Typography } from 'antd';
import { useLocation } from 'react-router-dom';

import FilterItemSelected from './FilterItemSelected';
import { getStartEndType } from './pickers/DateTimeRangePicker';
import RefreshButton from './RefreshButton';
import RequestButton from './RequestButton';
import Segments from './segments';
import { PagesPathnames } from '../../../../../../app/types/ad';
import {
  findObjectById,
} from '../../../../../../helpers/utils/functions';
import {
  generateListKeys,
  getParentKey,
  prepareData,
  migrateFiltersWithColor,
} from '../../../../../../helpers/utils/reports';
import { injectUtmSectionsIntoFilters } from '../../../../../../helpers/utils/utmSections';
import {
  calendarTypes,
  data,
  updateFilters,
} from '../../../../constants/filters';
import { routeNames } from '../../../../constants/routes';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { useLoadGroupingsData } from '../../../../hooks/useLoadGroupingsData';
import { useUtmSections } from '../../../../hooks/useUtmSections';

interface IUniversalFilterProps {
  isCompare: boolean;
  withSegment: boolean;
  disabled?: boolean;
  disableAttr?: boolean;
  filtersFilter?: string[];
  withLabel?: boolean;
  notParams?: boolean;
  withRefresh?: boolean;
  reqBtnDisabled?: boolean;
  setReqBtnDisabled?: Dispatch<SetStateAction<boolean>>;
  // Локальные фильтры для изоляции от глобального state (используются в модальном окне метрик)
  localFilters?: any[];
  setLocalFilters?: (filters: any[]) => void;
  // Режим Яндекс.Метрики — фильтры читаются/пишутся в state.filter.yandexMetric
  isYM?: boolean;
  // Кастомный заголовок фильтра (по умолчанию "Условия фильтра")
  filterName?: string;
}

const EXTRA_ROOT_NAMES = new Set<string>([
  'Доп. поля регистрации',
  'Доп. поля пользователя',
  'Доп. поля заказа',
  'Доп. поля заявки',
]);


/**
 * Рекурсивно фильтрует дерево, оставляя только узлы,
 * совпадающие с поиском, и их родителей.
 */
const filterTreeBySearch = (nodes: any[], search: string): any[] => {
  if (!search) return nodes;
  const reg = new RegExp(search, 'i');

  return nodes.reduce((acc: any[], node: any) => {
    const nameMatch = reg.test(node.name);

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTreeBySearch(node.children, search);
      // Parent: показываем если имя совпало или есть совпавшие дети
      if (nameMatch || filteredChildren.length > 0) {
        acc.push({ ...node, children: nameMatch ? node.children : filteredChildren });
      }
    } else {
      // Leaf: показываем только при совпадении имени
      if (nameMatch) {
        acc.push(node);
      }
    }
    return acc;
  }, []);
};

const shouldOpenFilterModal = (node: any) => {
  if (!node.isLeaf) {
    return false;
  }
  if (typeof node.onOk !== 'function') {
    return false;
  }
  return true;
};



// Базовый список ключей поиска по статическому дереву. Секции разобранных UTM-меток
// динамические, поэтому внутри компонента список пересчитывается (см. searchListKeys).
export const listKeys = generateListKeys(data);

const UniversalFilter: FC<IUniversalFilterProps> = ({
  isCompare,
  withSegment,
  filtersFilter,
  disabled = false,
  disableAttr = false,
  withLabel = true,
  notParams = false,
  withRefresh = false,
  setReqBtnDisabled,
  reqBtnDisabled = true,
  localFilters,
  setLocalFilters,
  isYM = false,
  filterName,
}) => {
  const { isNotParamsFilterVisible } = useTypedSelector((state) => state.meta);
  const { filterSelectorVisible } = useTypedSelector((state) => state.filter);
  const { filters: globalFilters, filterParent: globalFilterParent } = useTypedSelector(
    (state) => isYM ? state.filter.yandexMetric : state.filter
  );

  // Определяем режим работы: локальные фильтры (через пропсы) или глобальные (через Redux)
  const isLocalMode = localFilters !== undefined && setLocalFilters !== undefined;

  // Для локального режима используем переданные пропсы, иначе глобальный state
  const filters = isLocalMode ? localFilters : globalFilters;
  // Для локального режима filterParent не используется - стабильный пустой массив
  const emptyFilterParent = useMemo(() => [], []);
  const filterParent = isLocalMode ? emptyFilterParent : globalFilterParent;
  const locationState = useLocation();

  const {
    setMetaCompare,
    setFilterParent,
    setFilters,
    setCurrentSegment,
    setAllDataFetching,
    setMetaFilterUpdate,
    setAllBlocksFetching,
    setCurrentFunnel,
  } = useActions();

  const [ expandedKeys, setExpandedKeys ] = useState<string[]>([]);
  const [ searchValue, setSearchValue ] = useState('');
  const [ autoExpandParent, setAutoExpandParent ] = useState(true);
  const [rawData, setRawData] = useState<any[]>(data);
  const [ treeData, setTreeData ] = useState<any[]>([]);
  const [ filterVisible, setFilterVisible ] = useState(false);

 const prevPathRef = useRef(location.pathname);



  const { onLoadData } = useLoadGroupingsData(setRawData);

  // Секции разобранных UTM-меток регистрации — динамическая часть дерева фильтров
  const utmSections = useUtmSections();
  // Ключи для поиска считаем с учётом секций, иначе поиск не находит их пункты
  const searchListKeys = useMemo(
    () => generateListKeys(injectUtmSectionsIntoFilters(data, utmSections)),
    [ utmSections ]
  );


  const applyLazyFlags = (nodes: any[]): any[] => {
    if (!Array.isArray(nodes)) return nodes;

    nodes.forEach((node) => {
      const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;
      const isLazyParent = EXTRA_ROOT_NAMES.has(node.name);

      // просто помечаем, но title НЕ трогаем
      node.isLeaf = !isLazyParent && !hasChildren;

      if (hasChildren) {
        applyLazyFlags(node.children);
      }
    });

    return nodes;
  };

  useEffect(() => {
    // Не сбрасываем фильтры при смене страницы для локального режима и YM
    if (isLocalMode || isYM) return;
    
    const prev = prevPathRef.current;
    const next = locationState.pathname;

    if (prev !== next) {
      setFilters([]);
      setFilterParent([]);
      setSearchValue('');
      setExpandedKeys([]);
      setAutoExpandParent(false);
      setFilterVisible(false);
    }

    prevPathRef.current = next;
  }, [locationState.pathname, isLocalMode]);


  useEffect(() => {
   // let data_ = data;
   let data_ = rawData
    if (location.pathname === '/users/ad-reports') {
      data_ = rawData //updateFilters();
    }

    if (location.pathname === '/webinar/quality') {
      data_ = updateFilters().map(
        (filter: { children: { name: string }[] }) => {
          return {
            ...filter,
            children: filter.children.filter(
              (childFilter: { name: string }) =>
                !childFilter.name.startsWith('Период')
            ),
          };
        }
      );
    }

    // Добавляем фильтр «Тип действия» только для страницы «Посещение вебинара»
    if (location.pathname === '/webinar/attendance') {
      const actionTypeFilter = {
        name: 'Тип действия',
        description: 'Выберите тип действия участника на вебинаре.',
        full_name: 'Тип действия на веб.',
        id: 'webinar_action_type',
        type: 'list',
        items: [
          { id: 'in', name: 'Вход' },
          { id: 'out', name: 'Выход' },
        ],
      };
      data_ = data_.map((category: any) => {
        if (category.name === 'Вебинары') {
          const children = [...category.children];
          const actionIndex = children.findIndex(
            (child: any) => child.id === 'uwe_clicked_button_the_webinar'
          );
          children.splice(actionIndex + 1, 0, actionTypeFilter);
          return { ...category, children };
        }
        return category;
      });
    }

    // Достраиваем секции разобранных UTM-меток (после узла «UTM - метка регистрации»)
    data_ = injectUtmSectionsIntoFilters(data_, utmSections);

    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    // Клонируем данные перед prepareData, чтобы избежать мутации общих объектов
    // между разными экземплярами Filter на одной странице
    const clonedData = JSON.parse(JSON.stringify(filteredData));
    let  _data = prepareData(
      clonedData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [ '' ],
      '',
      filterParent,
      handleActivateReq,
      false,
      isYM,
      isLocalMode,
      localFilters,
      setLocalFilters
    );
    _data = applyLazyFlags(_data);

    if (searchValue) {
      _data = filterTreeBySearch(_data, searchValue);
    }

    if (_data.length === 1 && _data[0]?.key) {
      setAutoExpandParent(true);
      setExpandedKeys([ _data[0].key ]);
    }


    setTreeData(_data);
  }, [
    //data,
    rawData,
    searchValue,
    filters,
    filters?.length,
    isCompare,
    filterParent,
    location.pathname,
    filtersFilter,
    isLocalMode,
    utmSections,
  ]);

  useEffect(() => {
    if (!searchValue) {
      let data_ = data;
      if (location.pathname === '/users/ad-reports') {
        data_ = updateFilters();
      }
      setExpandedKeys([]);
      setAutoExpandParent(false);
      data_ = injectUtmSectionsIntoFilters(data_, utmSections);
      const filteredData = filtersFilter
        ? data_.filter((item: any) => filtersFilter.includes(item.name))
        : data_;
      // Клонируем данные перед prepareData, чтобы избежать мутации общих объектов
      const clonedData = JSON.parse(JSON.stringify(filteredData));
      let _data = prepareData(
        clonedData,
        searchValue,
        filters,
        disableAttr,
        '',
        '',
        '',
        '',
        [ '' ],
        '',
        filterParent,
        handleActivateReq,
        false,
        isYM,
        isLocalMode,
        localFilters,
        setLocalFilters
      );

      _data = applyLazyFlags(_data);

      setTreeData(_data);
    }
  }, [ searchValue, utmSections ]);

  useEffect(() => {
    if (!isCompare && !notParams) {
      setMetaCompare({ value: false });
    }
  }, [ isCompare ]);

  useEffect(() => {
    if (!filterSelectorVisible) {
      setExpandedKeys([]);
    }
  }, [ filterSelectorVisible ]);

  const onExpand = (_expandedKeys: any) => {
    setExpandedKeys(_expandedKeys);
    setAutoExpandParent(false);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value !== '') {
      const reg = new RegExp(value, 'i');
      const _treeData = treeData;

      let _expandedKeys: any = searchListKeys
        .map((item: any) => {
          if (item.name.search(reg) > -1) {
            return getParentKey(item.id, _treeData);
          }

          return null;
        })
        .filter(
          (item: any, i: number, self: any) => item && self.indexOf(item) === i
        );

      setExpandedKeys(_expandedKeys);
      setSearchValue(value);
      setAutoExpandParent(true);
    } else {
      setExpandedKeys([]);
      setSearchValue('');
      setAutoExpandParent(false);
    }
  };
  const onClearFilters = () => {
    if (isLocalMode) {
      // Очищаем локальные фильтры через callback
      setLocalFilters!([]);
    } else if (isYM) {
      setFilters({ data: [], isYM: true });
      setFilterParent({ data: [], isYM: true });
    } else {
      setCurrentSegment(null);
      setCurrentFunnel([]);
      setFilters([]);
      setFilterParent([]);
    }
  };

  const handleUpdateDashboardData = () => {
    setAllBlocksFetching({ value: true });
    setAllDataFetching({ value: true });
    setTimeout(() => {
        setAllBlocksFetching({ value: false });
        setMetaFilterUpdate({ value: false }); 
    }, 2000);
  };

  const handleActivateReq = (disable: boolean, withoutFilter = true) => {
    !withoutFilter && setFilterVisible(disable);
  };

  const isAutoRKTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    (Number(new URLSearchParams(location.search).get('tab')) === 1 ||
      !new URLSearchParams(location.search).get('tab'));

  const isAutoAdTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 2;

  const isManualAdTab =
    locationState.pathname === PagesPathnames.manualPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 1;

  // Фильтрация дерева при поиске — на уровне рендера, чтобы гарантированно скрыть несовпадающие узлы
  const displayTreeData = useMemo(() => {
    const base = disableAttr
      ? treeData.filter((item: any) => item.name !== 'Реклама' && item.name !== 'Регистрации')
      : treeData;
    return searchValue ? filterTreeBySearch(base, searchValue) : base;
  }, [ treeData, searchValue, disableAttr ]);

  // Мигрируем фильтры для корректного отображения (добавляем color если отсутствует)
  const migratedFilters = useMemo(() => migrateFiltersWithColor(filters), [filters]);

  // Группируем фильтры: все исключения одной сущности (по цвету) объединяем в один блок
  const filterRender = useMemo(() => {
    // Сначала подготовим список фильтров с их индексами, пропуская пустые
    const validFilters: { item: any; index: number }[] = [];
    migratedFilters.forEach((item: any, index: number) => {
      if (item.type === 'datetimerange' && !item.start && !item.end) {
        return;
      }
      validFilters.push({ item, index });
    });

    // Группируем ВСЕ исключения с одинаковым цветом в один блок
    // Сначала собираем исключения по цветам
    const excludesByColor: Map<string, { item: any; index: number }[]> = new Map();
    const regularFilters: { item: any; index: number }[] = [];
    
    validFilters.forEach(({ item, index }) => {
      const isExclude = item.id?.includes('not_');
      const color = item.color || null;
      
      if (isExclude && color) {
        if (!excludesByColor.has(color)) {
          excludesByColor.set(color, []);
        }
        excludesByColor.get(color)!.push({ item, index });
      } else {
        regularFilters.push({ item, index });
      }
    });

    // Формируем итоговый список: сначала обычные фильтры, потом группы исключений по цветам
    const result: React.ReactNode[] = [];
    let isFirst = true;

    // Рендерим обычные фильтры
    regularFilters.forEach(({ item, index: itemIndex }) => {
      result.push(
        <Fragment key={itemIndex}>
          {!isFirst && (
            <Typography.Text type="secondary">и</Typography.Text>
          )}
          <FilterItemSelected
            arrayIndex={itemIndex}
            id={item.id}
            itemData={item}
            full_name={item.full_name}
            isLocalMode={isLocalMode}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            isYM={isYM}
            {...item}
          />
        </Fragment>
      );
      isFirst = false;
    });

    // Рендерим группы исключений по цветам
    excludesByColor.forEach((items, color) => {
      result.push(
        <Fragment key={`exclude-${color}`}>
          {!isFirst && (
            <Typography.Text type="secondary">и</Typography.Text>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
            <b style={{ fontWeight: 600, color: '#ff0000', marginRight: 4, marginLeft: 4, alignSelf: 'center' }}>
              НЕ
            </b>
            <span 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '2px',
                gap: '4px',
                border: '2px solid #ff4d4f',
                borderRadius: '8px',
              }}
            >
              {items.map(({ item, index: itemIndex }, i) => (
                <Fragment key={itemIndex}>
                  {i !== 0 && (
                    <Typography.Text type="secondary" style={{ margin: '0 4px' }}>и</Typography.Text>
                  )}
                  <FilterItemSelected
                    arrayIndex={itemIndex}
                    id={item.id}
                    itemData={item}
                    full_name={item.full_name}
                    isLocalMode={isLocalMode}
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                    isYM={isYM}
                    {...item}
                  />
                </Fragment>
              ))}
            </span>
          </span>
        </Fragment>
      );
      isFirst = false;
    });

    return result;
  }, [migratedFilters, isLocalMode, localFilters, setLocalFilters, isYM]);

    


  return (
    <>
      {withSegment ? (
        <Segments disabled={disabled} setBtnDisabled={handleActivateReq} />
      ) : null}
      <span data-help-id="params-filters" style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
      {withRefresh && <RefreshButton />}
      {filters?.length > 0 && (
        <Button
          onClick={() => onClearFilters()}
          icon={<CloseOutlined />}
          title="Очистить"
        />
      )}
      {withLabel && (
        <Typography.Text type="secondary">
          {filterName ?? 'Условия фильтра'}:&nbsp;
        </Typography.Text>
      )}

      {filters?.length !== 0 &&
        filterRender
      }
      {(notParams || !isNotParamsFilterVisible) && (
        <Popover
          content={
            <Tree.DirectoryTree
              blockNode
              switcherIcon={<DownOutlined />}
              showIcon={false}
              loadData={onLoadData}
              selectable
              onSelect={(_keys, info) => {
                const node: any = info.node;
                if (!shouldOpenFilterModal(node)) return;
                node.onOk?.(false);
              }}
              treeData={displayTreeData}
              onExpand={onExpand}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              key={'name'}
            />
          }
          title={
            <Input
              onChange={(e) => onSearch(e)}
              placeholder="Найти условие1"
              allowClear
            />
          }
          trigger="click"
          placement="bottom"
          overlayClassName="filter-popover"
          open={filterVisible}
          onOpenChange={setFilterVisible}
        >
          <Button icon={<PlusOutlined />} type="primary" shape="circle" />
        </Popover>
      )}
      {!notParams && setReqBtnDisabled && (
        <RequestButton
          setUpdateDashboard={handleUpdateDashboardData}
          btnDisabled={reqBtnDisabled}
          setBtnDisabled={setReqBtnDisabled}
        />
      )}
      </span>
    </>
  );
};

export default UniversalFilter;
